import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const MASTER_ID = '7333de0f-e17e-4c3c-a6a5-5feafd753a99';

type Update = {
  id: string;
  source_law_pack_name: string;
  title: string;
  summary: string;
  category: string;
  severity: string;
  published_at: string;
};

export default function LawPackUpdatesManage() {
  const { user, loading: authLoading } = useAuth();
  const [lawPackOptions, setLawPackOptions] = useState<{ id: string; name: string; region: string | null }[]>([]);
  const [existing, setExisting] = useState<Update[]>([]);
  const [form, setForm] = useState({
    source_law_pack_id: '',
    source_law_pack_name: '',
    title: '',
    summary: '',
    category: 'compliance',
    severity: 'medium',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.id !== MASTER_ID) return;
    (async () => {
      const { data: packs } = await supabase
        .from('law_packs')
        .select('id, name, region')
        .order('name');
      // One canonical row per name+region — the RLS policy matches tenant packs
      // against this canonical row by id.
      const seen = new Set<string>();
      const options: { id: string; name: string; region: string | null }[] = [];
      for (const p of packs ?? []) {
        const key = `${p.name}::${p.region ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        options.push({ id: p.id, name: p.name, region: p.region ?? null });
      }
      setLawPackOptions(options);
      const { data: ex } = await supabase
        .from('law_pack_updates')
        .select('id, source_law_pack_name, title, summary, category, severity, published_at')
        .order('published_at', { ascending: false });
      setExisting(ex ?? []);
    })();
  }, [user]);

  if (authLoading) return null;
  if (!user || user.id !== MASTER_ID) return <Navigate to="/law-packs" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.source_law_pack_id || !form.source_law_pack_name || !form.title || !form.summary) {
      toast.error('Fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('law_pack_updates')
        .insert({ ...form, published_by: user.id })
        .select()
        .single();
      if (error) throw error;
      toast.success('Update published');
      setExisting(prev => [data as Update, ...prev]);
      setForm({
        source_law_pack_id: '',
        source_law_pack_name: '',
        title: '',
        summary: '',
        category: 'compliance',
        severity: 'medium',
      });
    } catch (err: any) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this update? Tenants that already applied it will keep their record.')) return;
    const { error } = await supabase.from('law_pack_updates').delete().eq('id', id);
    if (error) return toast.error(error.message);
    setExisting(prev => prev.filter(u => u.id !== id));
    toast.success('Deleted');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="container mx-auto max-w-4xl space-y-6">
        <PageHeader
          title="Publish Law Pack Updates"
          description="Author regulatory updates that all tenants with the target law pack will see."
          backTo="/law-packs"
          backLabel="Back to Law Packs"
        />

        <Card>
          <CardHeader><CardTitle>New Update</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Target Law Pack Name *</Label>
                <Select
                  value={form.source_law_pack_id}
                  onValueChange={v => {
                    const pack = lawPackOptions.find(p => p.id === v);
                    setForm(f => ({
                      ...f,
                      source_law_pack_id: v,
                      source_law_pack_name: pack?.name ?? '',
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select a law pack…" /></SelectTrigger>
                  <SelectContent>
                    {lawPackOptions.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.region ? ` · ${p.region}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <Label>Summary *</Label>
                <Textarea rows={4} value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['temperature', 'monitoring', 'documentation', 'safety', 'compliance', 'training'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity</Label>
                  <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['low', 'medium', 'high', 'critical'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="cta-primary">
                {submitting ? 'Publishing…' : 'Publish Update'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Published Updates</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {existing.length === 0 && (
              <p className="text-sm text-muted-foreground">No updates published yet.</p>
            )}
            {existing.map(u => (
              <div key={u.id} className="flex items-start justify-between gap-4 border rounded-md p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium truncate">{u.title}</p>
                    <Badge variant="outline" className="capitalize">{u.severity}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{u.summary}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    → {u.source_law_pack_name} · {new Date(u.published_at).toLocaleDateString()}
                  </p>
                </div>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(u.id)}>Delete</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}